<template>
    <div>
        <Toast />
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center pt-4 pb-2">
            <div class="hidden sm:block" style="width: 33%"></div>
            <div class="flex-1 flex justify-center">
                <InputText v-model="searchQuery" placeholder="Search partner or operator..."
                    class="w-full pl-10 transition-all duration-300" :class="{ 'search-highlight': searchHighlight }"
                    ref="searchInput" />
            </div>
            <div class="flex justify-end sm:w-1/3">
                <Button label="Add Voonix Advertiser" @click="openCreateDialog" severity="primary"
                    class="w-full sm:w-auto" />
            </div>
        </div>
        <Dialog v-model:visible="visible" modal dismissableMask closeOnEscape class="w-full 2xl:w-4/5 modal-content">
            <Create :mode="formMode" :advertiser="selectedAdvertiser" @brandAdded="handleAdvertiserSaved"
                @brandUpdated="handleAdvertiserSaved" />
        </Dialog>
        <div class="flex justify-center items-center  pt-8">
            <div class="w-full  bg-white rounded-lg shadow-md">
                <DataTable :value="filteredVoonixAdvertisers" dataKey="_id" scrollable :scrollHeight="'70vh'"
                    v-model:expandedRows="expandedRows" :rowStyle="() => ({ background: '#27272A' })">

                    <!-- Name column (shows count of advertisers) -->
                    <Column field="spaceAdvertiserLogin.partner" header="Partner" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>

                    <Column field="spaceAdvertiserLogin.affiliateSystem.name" header="Affiliate System"
                        :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div class="flex items-center gap-2">
                                <span>{{ data.spaceAdvertiserLogin.affiliateSystem.name }}</span>
                                <a v-if="loginFieldWarning(data.spaceAdvertiserLogin)"
                                   :href="loginFieldWarning(data.spaceAdvertiserLogin).href"
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   v-tooltip.top="loginFieldWarning(data.spaceAdvertiserLogin).tooltip"
                                   class="text-amber-400 hover:text-amber-300 no-underline">
                                    <i class="pi pi-exclamation-triangle" />
                                </a>
                            </div>
                        </template>
                    </Column>
                    <!-- Login URL column -->
                    <Column header="Login URL" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <Button label="Login" icon="pi pi-external-link" link
                                @click="openUrl(data.spaceAdvertiserLogin.loginUrl)"
                                v-tooltip.bottom="'Open login page in new tab'" severity="secondary" />
                            <Button icon="pi pi-user" severity="secondary"
                                @click="copyToClipboard(data.spaceAdvertiserLogin.username)"
                                class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy username'" />
                            <Button icon="pi pi-key" severity="secondary"
                                @click="copyToClipboard(data.spaceAdvertiserLogin.password)"
                                class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy password'" />
                        </template>
                    </Column>
                    <Column header="Salid" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div class="flex items-center gap-2">
                                <span>{{ data.spaceAdvertiserLogin.salid }}</span>
                                <Button
                                    icon="pi pi-link"
                                    size="small"
                                    severity="secondary"
                                    text
                                    v-tooltip.top="'View postback URLs'"
                                    @click="openPostbacksModal(data)" />
                            </div>
                        </template>
                    </Column>
                    <!-- Payment Method column (shows aggregated info) -->
                    <Column header="Postback" :headerStyle="headerStyles" :style="{ width: '100px' }">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div class="flex items-center gap-2">
                                <Tag
                                    :value="data.spaceAdvertiserLogin.postbacksSet ? 'Setup' : 'Not Set'"
                                    :severity="data.spaceAdvertiserLogin.postbacksSet ? 'success' : 'danger'"
                                    @click="handlePostbacksSet(data)"
                                    style="cursor: pointer;"
                                />
                            </div>
                        </template>
                    </Column>



                    <!-- Actions column -->
                    <Column header="Actions" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <Button icon="pi pi-plus" @click="openAddOperatorDialog(data)" class="p-button-text"
                                v-tooltip.top="'Add new operator'" severity="secondary" />
                        </template>
                    </Column>

                    <template #expansion="{ data }">
                        <div class="p-4">
                            <DataTable :value="data.advertisers" dataKey="_id"  :showHeader="false">
                                <Column field="name" header="Operator" :headerStyle="hideHeaderStyles"></Column>
                                <Column header="Login URL" :headerStyle="hideHeaderStyles">
                                    <template v-if="isLoading" #body>
                                        <Skeleton></Skeleton>
                                    </template>
                                    <template v-else #body="{ data }">
                                        <Button label="Login" icon="pi pi-external-link" link
                                            @click="openUrl(data.login.loginUrl)"
                                            v-tooltip.bottom="'Open login page in new tab'" severity="secondary" />
                                        <Button icon="pi pi-user" severity="secondary"
                                            @click="copyToClipboard(data.login.username)"
                                            class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy username'" />
                                        <Button icon="pi pi-key" severity="secondary"
                                            @click="copyToClipboard(data.login.password)"
                                            class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy password'" />
                                    </template>
                                </Column>
                                <Column header="Payer Info" :headerStyle="hideHeaderStyles">
                                    <template #body="{ data: advertiser }">
                                        <Button v-if="advertiser.payerId" icon="pi pi-user" label="View Payer" text
                                            severity="primary"
                                            @click="showPayerInfo(advertiser.payerId, advertiser.name)"
                                            v-tooltip.bottom="'Click to view payer details'" />
                                        <Tag v-else severity="danger" value="Not Set" />
                                    </template>
                                </Column>
                                <Column header="Actions" :headerStyle="hideHeaderStyles">
                                    <template #body="{ data: advertiser }">
                                        <Button icon="pi pi-pencil" @click="openEditDialog(advertiser)"
                                            class="p-button-text" v-tooltip.top="'Edit Advertiser'"
                                            severity="secondary" />
                                    </template>
                                </Column>
                            </DataTable>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
        <PayerDataDialog v-model:show="showPayerDialog" :payer="selectedPayer" />
        <PostbacksModal
            v-model:visible="showPostbacksModal"
            :affiliateSystem="postbacksModalSystem"
            :salid="postbacksModalSalid"
            :partner="postbacksModalPartner"
            :loginUrl="postbacksModalLoginUrl" />
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import VoonixAdvertiserService from "@/services/VoonixAdvertiserService";
import Create from "./Create.vue";
import { openUrl } from '@/utils/url';
import { useToast } from "primevue/usetoast";
import { headerStyles, nestedHeaderStyles, hideHeaderStyles } from '@/global/headerStyles';
import Utils from '@/utils';
import { getPaymentMethodSeverity, getPaymentMethodName } from '@/utils/paymentMethods';
import { validateLoginFields, getHelpSectionForSystem } from '@/utils/affiliate-system-fields';
import { useRouter } from 'vue-router';
import PayerDataDialog from './PayerDataDialog.vue';
import PostbacksModal from '@/components/PostbacksModal.vue';

const router = useRouter();

/**
 * Given a SpaceAdvertiserLogin doc, return either null (all required fields
 * present) or { tooltip, href } describing the action needed. Used to render
 * the amber warning icon next to the affiliate-system name on rows whose
 * setup is incomplete — the icon links to the relevant /help section.
 */
function loginFieldWarning(sal) {
    if (!sal || !sal.affiliateSystem) return null;
    const systemName = sal.affiliateSystem.name;
    const errors = validateLoginFields(systemName, sal);
    const missing = Object.keys(errors);
    if (!missing.length) return null;

    const slug = getHelpSectionForSystem(systemName);
    const href = slug
        ? router.resolve({ name: 'Help', query: { section: slug } }).href
        : router.resolve({ name: 'Help' }).href;
    const missingList = missing.map(f => errors[f]).join(' ');
    return { tooltip: `Missing required setup: ${missingList} Click for docs.`, href };
}

const toast = useToast();

const showPostbacksModal = ref(false);
const postbacksModalSystem = ref('');
const postbacksModalSalid = ref(null);
const postbacksModalPartner = ref('');
const postbacksModalLoginUrl = ref('');

function openPostbacksModal(data) {
    postbacksModalSystem.value = data.spaceAdvertiserLogin.affiliateSystem.name;
    postbacksModalSalid.value = Number(data.spaceAdvertiserLogin.salid);
    postbacksModalPartner.value = data.spaceAdvertiserLogin.partner;
    postbacksModalLoginUrl.value = data.spaceAdvertiserLogin.loginUrl;
    showPostbacksModal.value = true;
}
const voonixAdvertisers = ref(new Array(10));
const searchQuery = ref('');
const searchInput = ref(null);
const searchHighlight = ref(false);

const isLoading = ref(true);
const visible = ref(false);
const formMode = ref('create');
const selectedAdvertiser = ref(null);

const showPayerDialog = ref(false);
const selectedPayer = ref(null);

const expandedRows = ref({});

const openCreateDialog = () => {
    formMode.value = 'create';
    selectedAdvertiser.value = null;
    visible.value = true;
};

const openEditDialog = (advertiser) => {
    formMode.value = 'edit';
    selectedAdvertiser.value = advertiser;
    visible.value = true;
    // Shareable link straight to this integration's editor.
    setQueryParam('open', advertiser?._id ?? null);
};

// Set or clear one query param without touching the rest.
function setQueryParam(key, value) {
    const query = { ...router.currentRoute.value.query };
    if (value === null || value === undefined || value === '') delete query[key];
    else query[key] = String(value);
    router.replace({ query });
}

// Closing the dialog any way — X, Escape, mask, or after a save — drops `open`.
// Create / add-operator never set it, so clearing is a no-op for those.
watch(visible, (isOpen) => {
    if (!isOpen) setQueryParam('open', null);
});

// Keep ?search= in step with the box, the same way /advertisers already does.
watch(searchQuery, (q) => setQueryParam('search', q || null));

const openAddOperatorDialog = (partner) => {
    formMode.value = 'create';
    const advertiser = {
        currency: 'EUR',
        spaceAdvertiserId: partner.spaceAdvertiserLogin._id,
        login: {
            loginUrl: partner.spaceAdvertiserLogin.loginUrl,
            password: partner.spaceAdvertiserLogin.password,
            username: partner.spaceAdvertiserLogin.username,
            affiliateSystem: partner.spaceAdvertiserLogin.affiliateSystem,
            key1: partner.spaceAdvertiserLogin.key1,
            key2: partner.spaceAdvertiserLogin.key2,
        },
        partner: partner.spaceAdvertiserLogin.partner,
        type: 'REV-CPA',
        rev: '100',
        cpa: '0'
    };
    selectedAdvertiser.value = advertiser;
    visible.value = true;
}

const handleAdvertiserSaved = async () => {
    visible.value = false;
    isLoading.value = true;
    await fetchBrands();
};

async function fetchBrands() {
    try {
        await VoonixAdvertiserService.getAll()
            .then((data) => {
                voonixAdvertisers.value = data.data;
            });
    } catch (err) {
        console.error(err);
    } finally {
        isLoading.value = false;
    }
}

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: 'Text copied to clipboard',
            life: 2000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy text',
            life: 3000
        });
    }
};


const showPayerInfo = (payer, advertiserName) => {
    selectedPayer.value = { ...payer, advertiserName };
    showPayerDialog.value = true;
};

const filteredVoonixAdvertisers = computed(() => {
    if (!searchQuery.value) return voonixAdvertisers.value;
    const q = searchQuery.value.toLowerCase();
    return voonixAdvertisers.value.filter(group =>
        // Match any advertiser name
        group.advertisers.some(advertiser =>
            advertiser.name?.toLowerCase().includes(q)
        )
        // Or match the partner field
        || group.spaceAdvertiserLogin?.partner?.toLowerCase().includes(q)
    );
});

function handleKeyDown(event) {
    // Check for Ctrl+F (or Cmd+F on Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        // Prevent the default browser search
        event.preventDefault();

        // Focus the input using Approach 2 ($el property)
        if (searchInput.value && searchInput.value.$el) {
            searchInput.value.$el.focus();

            // Trigger highlight effect
            searchHighlight.value = true;

            // Remove highlight after animation completes
            setTimeout(() => {
                searchHighlight.value = false;
            }, 1000); // 1 second duration
        }
    }
}

onMounted(async () => {
    const query = router.currentRoute.value.query;
    if (query.search) searchQuery.value = String(query.search);

    await fetchBrands();
    document.addEventListener('keydown', handleKeyDown);

    voonixAdvertisers.value.forEach(group => {
        expandedRows.value[group._id] = true;
    });

    // Deep link: ?open=<voonixAdvertiserId>. Rows are grouped by login, so the
    // integration is looked up inside each group's advertisers after the fetch.
    if (query.open) {
        const wanted = String(query.open);
        const match = (voonixAdvertisers.value || [])
            .flatMap(group => group?.advertisers || [])
            .find(a => String(a?._id) === wanted);
        if (match) {
            openEditDialog(match);
        } else {
            toast.add({ severity: 'warn', summary: 'Voonix advertiser not found',
                detail: `No integration with id ${wanted} — it may have been removed.`, life: 5000 });
            setQueryParam('open', null);
        }
    }
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
});

const handlePostbacksSet = (data) => {
    VoonixAdvertiserService.updatePostbacksSet(data.spaceAdvertiserLogin._id, !data.spaceAdvertiserLogin.postbacksSet)
        .then(() => {
            console.log('Postbacks set updated');
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Postbacks set updated',
                life: 2000
            });
            //update the voonixAdvertisers array
            voonixAdvertisers.value = voonixAdvertisers.value.map(group => {
                if (group.spaceAdvertiserLogin._id === data.spaceAdvertiserLogin._id) {
                    group.spaceAdvertiserLogin.postbacksSet = !group.spaceAdvertiserLogin.postbacksSet;
                }
                return group;
            });
        }).catch((err) => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update postbacks set',
                life: 3000
            });
        });
};

</script>

<style scoped>
.link-class {
    text-decoration: underline;
    color: blue;
    cursor: pointer;
}

.link-class:hover {
    color: darkblue;
}

@keyframes pulse-border {
    0% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
    }

    70% {
        box-shadow: 0 0 0 25px rgba(59, 130, 246, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
    }
}

.search-highlight {
    animation: pulse-border 1s ease-out;
    border-color: #3b82f6;
    /* blue-500 */
    background-color: rgba(219, 234, 254, 0.5);
    /* blue-100 with transparency */
}

/* :deep(tr[data-pc-section="rowexpansion"] > td[data-pc-section="rowexpansioncell"]) {
    background: #27272A !important;
} */

:deep(tr[data-pc-section="rowexpansion"] > td[data-pc-section="rowexpansioncell"]) > div {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}
</style>